/**
 * Desktop native bridge (WP7.8, DECISIONS.md ADR-085).
 *
 * Talks to the Tauri 2 Rust shell (`apps/desktop/src-tauri`) for the things a plain browser tab
 * cannot do: a serial scanner bridge, raw ZPL label printing, and a local SQLite reference-data
 * cache + offline command outbox for field operations. Keyboard-wedge USB/Bluetooth scanners need
 * none of this — they type into whatever input has focus and already work in the plain web UI.
 *
 * This file is the ONLY place that is allowed to depend on `@tauri-apps/api`/`@tauri-apps/plugin-
 * updater`. Every exported function checks {@link isDesktopRuntime} first and no-ops (or, for the
 * offline outbox's *write* operations — see below — throws a clear error) when running in a plain
 * browser tab, so pages can call these unconditionally without an `if (window.__TAURI__)` scattered
 * through the UI. Pages/router/i18n wiring that calls into this module belongs to whichever track
 * owns the screen (Inventory count/scan screens, printing) — see the WP7.8 report for the specific
 * hooks this module expects to be called from.
 *
 * SQLite here is a **cache/replica and offline command outbox only** — never the ERP source of
 * truth (AGENTS.md; `specs/01-architecture.md` "Desktop/offline"). Only field-operation *drafts*
 * (physical count lines, roll scan/receipt drafts) are ever queued offline; this module must never
 * be used to queue a direct accounting/stock posting command.
 */

import { invoke } from '@tauri-apps/api/core';
import { listen, type UnlistenFn } from '@tauri-apps/api/event';
import { check as checkUpdaterForUpdate } from '@tauri-apps/plugin-updater';

/** True only inside the Tauri desktop shell — false for the same web build opened in an ordinary
 * browser tab. Checked via the IPC bridge global Tauri injects into `window`, so it is correct even
 * before any `@tauri-apps/api` call has been made (no async handshake needed). */
export function isDesktopRuntime(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

// ---- scanner --------------------------------------------------------------------------------

export interface SerialPortInfo {
  portName: string;
  description: string | null;
}

export interface ScanEvent {
  code: string;
  portName: string;
  receivedAt: string;
}

/** Serial ports currently visible to the OS. Empty outside the desktop shell — never throws, so a
 * "connect scanner" settings screen can render its port picker unconditionally. */
export async function listScannerPorts(): Promise<SerialPortInfo[]> {
  if (!isDesktopRuntime()) return [];
  return invoke<SerialPortInfo[]>('scanner_list_ports');
}

/** Opens `portName` and starts emitting {@link onScan} events for each barcode frame read from it.
 * A no-op outside the desktop shell (there is no serial port to open). */
export async function connectScanner(portName: string, baudRate: number): Promise<void> {
  if (!isDesktopRuntime()) return;
  await invoke('scanner_connect', { portName, baudRate });
}

export async function disconnectScanner(): Promise<void> {
  if (!isDesktopRuntime()) return;
  await invoke('scanner_disconnect');
}

/** The currently-connected port name, or `null` if none — always `null` outside the desktop shell. */
export async function scannerStatus(): Promise<string | null> {
  if (!isDesktopRuntime()) return null;
  return invoke<string | null>('scanner_status');
}

/** Subscribes to native scan events. Returns an unsubscribe function; outside the desktop shell the
 * handler is simply never called and the returned unsubscribe is a no-op, so callers can wire this
 * up unconditionally in an `onMounted`/`onUnmounted` pair. */
export function onScan(handler: (event: ScanEvent) => void): () => void {
  if (!isDesktopRuntime()) return () => {};
  let unlisten: UnlistenFn | undefined;
  let cancelled = false;
  void listen<ScanEvent>('desktop://scan', (event) => handler(event.payload)).then((fn) => {
    if (cancelled) {
      fn();
    } else {
      unlisten = fn;
    }
  });
  return () => {
    cancelled = true;
    unlisten?.();
  };
}

// ---- label printer ----------------------------------------------------------------------------

export type PrinterTarget =
  { kind: 'network'; host: string; port: number } | { kind: 'windowsUsb'; printerName: string };

export interface PrintResult {
  bytesSent: number;
}

/** Sends a finished ZPL string (produced server-side by the printing module, WP7.7) to a physical
 * label printer. Rejects outside the desktop shell — there is no local printer to reach from a
 * browser tab, so this is a real error rather than a silent no-op (a caller offering a "print
 * label" button should itself hide/disable it when {@link isDesktopRuntime} is false). */
export async function printZpl(target: PrinterTarget, zpl: string): Promise<PrintResult> {
  if (!isDesktopRuntime()) {
    throw new Error('Label printing requires the Jervis ERP desktop app.');
  }
  return invoke<PrintResult>('print_zpl', { target, zpl });
}

// ---- reference-data cache ----------------------------------------------------------------------

export interface CachedReference {
  collection: string;
  key: string;
  payloadJson: string;
  version: string;
  /** RFC3339 timestamp of when this row was last written to the local cache. */
  fetchedAt: string;
}

/** Upserts one cached reference-data row (an item, a UOM, a location, an open count session, ...).
 * A no-op outside the desktop shell — there is no local cache to populate. */
export async function putCachedReference(
  collection: string,
  key: string,
  payloadJson: string,
  version: string,
): Promise<void> {
  if (!isDesktopRuntime()) return;
  await invoke('cache_put', { collection, key, payloadJson, version });
}

/** All cached rows for `collection`. Empty outside the desktop shell. */
export async function listCachedReferences(collection: string): Promise<CachedReference[]> {
  if (!isDesktopRuntime()) return [];
  return invoke<CachedReference[]>('cache_list', { collection });
}

export async function clearCachedCollection(collection: string): Promise<void> {
  if (!isDesktopRuntime()) return;
  await invoke('cache_clear', { collection });
}

/** Pure staleness check — deliberately not baked into the Rust store, since "how stale is too
 * stale" is a per-screen UX decision, not a storage-layer policy. `fetchedAt` is an RFC3339
 * timestamp as stored by {@link putCachedReference}/returned by {@link listCachedReferences}. */
export function isReferenceStale(fetchedAt: string, maxAgeMs: number, now: Date = new Date()): boolean {
  const fetchedAtMs = Date.parse(fetchedAt);
  if (Number.isNaN(fetchedAtMs)) return true;
  return now.getTime() - fetchedAtMs > maxAgeMs;
}

// ---- offline command outbox --------------------------------------------------------------------

export type OutboxCommandStatus = 'PENDING' | 'SENDING' | 'FAILED' | 'COMPLETED';

export interface OutboxCommand {
  id: string;
  idempotencyKey: string;
  commandType: string;
  method: string;
  endpoint: string;
  payloadJson: string;
  status: OutboxCommandStatus;
  responseStatus: number | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

function requireDesktopRuntime(action: string): void {
  if (!isDesktopRuntime()) {
    throw new Error(`${action} requires the Jervis ERP desktop app (no local outbox in a browser tab).`);
  }
}

/**
 * Queues a field-operation command (a physical count-line submission, a roll scan/receipt draft)
 * for replay through the existing server REST endpoint once online. `commandType` is a short label
 * for the UI/audit trail (e.g. `'inventory.count.recordCounts'`); `method`/`endpoint` describe the
 * exact server call to make on replay; `payload` is the JSON-serializable request body.
 *
 * Throws outside the desktop shell rather than silently discarding the command — an offline capture
 * screen must itself check {@link isDesktopRuntime} (or fall back to an immediate online submit)
 * before offering "save for later sync" as an option.
 *
 * MUST NEVER be called for a direct accounting/stock posting command — only for drafts/count lines
 * the server itself still validates and can reject (AGENTS.md rule 11, `specs/10` "Idempotency").
 */
export async function enqueueOfflineCommand(
  commandType: string,
  method: string,
  endpoint: string,
  payload: unknown,
): Promise<OutboxCommand> {
  requireDesktopRuntime('Queuing an offline command');
  return invoke<OutboxCommand>('outbox_enqueue', {
    commandType,
    method,
    endpoint,
    payloadJson: JSON.stringify(payload),
  });
}

/** Every queued command, in replay order, regardless of status — for a "sync status" review screen. */
export async function listOutboxCommands(): Promise<OutboxCommand[]> {
  if (!isDesktopRuntime()) return [];
  return invoke<OutboxCommand[]>('outbox_list_all');
}

/** Deletes a `FAILED` command — the user's explicit "discard". Resolves `false` (nothing deleted) for
 * any other status: a still-queued draft is never silently dropped. */
export async function discardFailedOutboxCommand(id: string): Promise<boolean> {
  requireDesktopRuntime('Discarding an offline command');
  return invoke<boolean>('outbox_discard_failed', { id });
}

/** Only commands still eligible for replay (`PENDING`), in the order they must be replayed. */
export async function listPendingOutboxCommands(): Promise<OutboxCommand[]> {
  if (!isDesktopRuntime()) return [];
  return invoke<OutboxCommand[]>('outbox_list_pending');
}

export async function getOutboxCommand(id: string): Promise<OutboxCommand | null> {
  if (!isDesktopRuntime()) return null;
  return invoke<OutboxCommand | null>('outbox_get', { id });
}

async function markOutboxSending(id: string): Promise<void> {
  requireDesktopRuntime('Marking an offline command as sending');
  await invoke('outbox_mark_sending', { id });
}

async function markOutboxCompleted(id: string, responseStatus: number): Promise<void> {
  requireDesktopRuntime('Marking an offline command as completed');
  await invoke('outbox_mark_completed', { id, responseStatus });
}

/** A definitive server rejection (409/422) — terminal, never auto-retried/auto-merged. */
async function markOutboxFailed(id: string, responseStatus: number, error: string): Promise<void> {
  requireDesktopRuntime('Marking an offline command as failed');
  await invoke('outbox_mark_failed', { id, responseStatus, error });
}

/** A transient failure (offline/timeout/5xx) — reverts to PENDING for the next sync pass. */
async function revertOutboxToPending(id: string, error: string): Promise<void> {
  requireDesktopRuntime('Reverting an offline command to pending');
  await invoke('outbox_revert_to_pending', { id, error });
}

/** One HTTP call's outcome, as reported by whatever fetch client the caller of
 * {@link replayOutboxCommands} already uses (this module makes no network calls itself — it has no
 * server base URL or auth token). */
export interface OutboxReplayOutcome {
  ok: boolean;
  status: number;
  /** Human-readable server problem (RFC 9457 `type` code + detail) for a rejection, stored as the
   * command's `lastError` so the review screen can show *why* the server said no. */
  detail?: string;
}

/** HTTP statuses that are a definitive answer to *this* command — retrying the identical request can
 * never succeed, so the command goes `FAILED` (terminal, user-reviewed) instead of looping through
 * `PENDING` forever. 409/422 are the ADR-085 cases; 400/403/404/410/412 are added for the same reason
 * (ADR-085 Amendment). 401/408/429/5xx stay transient: a re-login or a retry can fix them. */
export function isDefinitiveRejection(status: number): boolean {
  return [400, 403, 404, 409, 410, 412, 422].includes(status);
}

export type OutboxReplayFn = (command: OutboxCommand) => Promise<OutboxReplayOutcome>;

export interface OutboxReplaySummary {
  completed: string[];
  /** Definitively rejected by the server (409/422) — left FAILED for the user to review; never
   * retried automatically. */
  failed: string[];
  /** Reverted to PENDING for the next sync pass (offline, timeout, 5xx, or any other non-2xx/
   * non-409/422 response). */
  stillPending: string[];
}

/**
 * Replays every currently-PENDING command, in strict insertion order, using the caller-supplied
 * `replay` function (the page/composable that owns an authenticated fetch client and already knows
 * how to attach the `idempotency-key` header from `command.idempotencyKey`). Each command's status
 * is updated in the local store as it is attempted:
 *
 * - 2xx -> `COMPLETED`;
 * - a definitive rejection (409/422 and other non-retryable 4xx, see {@link isDefinitiveRejection}) -> `FAILED`, terminal, never auto-merged/retried
 *   (AGENTS.md rule 11, no Last-Write-Wins) — surfaced back to the user for review;
 * - anything else (network error thrown by `replay`, timeout, 5xx, ...) -> reverted to `PENDING` so
 *   the next sync pass retries it. Replay is at-least-once and safe because every retry reuses the
 *   same `idempotency-key`, so the server's own idempotency handling (`platform/kernel`) returns the
 *   original result instead of double-applying an already-accepted command.
 *
 * A no-op returning empty arrays outside the desktop shell.
 */
export async function replayOutboxCommands(replay: OutboxReplayFn): Promise<OutboxReplaySummary> {
  const summary: OutboxReplaySummary = { completed: [], failed: [], stillPending: [] };
  if (!isDesktopRuntime()) return summary;

  const pending = await listPendingOutboxCommands();
  for (const command of pending) {
    await markOutboxSending(command.id);
    try {
      const outcome = await replay(command);
      if (outcome.ok) {
        await markOutboxCompleted(command.id, outcome.status);
        summary.completed.push(command.id);
      } else if (isDefinitiveRejection(outcome.status)) {
        await markOutboxFailed(
          command.id,
          outcome.status,
          outcome.detail ?? `Server rejected the command (HTTP ${outcome.status}).`,
        );
        summary.failed.push(command.id);
      } else {
        await revertOutboxToPending(command.id, `Unexpected response (HTTP ${outcome.status}); will retry.`);
        summary.stillPending.push(command.id);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      await revertOutboxToPending(command.id, message);
      summary.stillPending.push(command.id);
    }
  }
  return summary;
}

// ---- signed updater -----------------------------------------------------------------------------

export interface DesktopUpdateInfo {
  version: string;
  date: string | null;
  body: string | null;
}

/** Checks the configured update endpoint (`tauri.conf.json`'s `plugins.updater`, see
 * `docs/desktop.md` for signing-key setup) for a newer, Ed25519-signed release. Returns `null` if
 * already up to date, or always outside the desktop shell. Downloading/installing the update is a
 * deliberate separate step left to the calling UI (e.g. behind an explicit "Update now" button)
 * rather than done implicitly by this check. */
export async function checkForDesktopUpdate(): Promise<DesktopUpdateInfo | null> {
  if (!isDesktopRuntime()) return null;
  const update = await checkUpdaterForUpdate();
  if (!update) return null;
  return { version: update.version, date: update.date ?? null, body: update.body ?? null };
}
